// =============================================================================
// BP-08-05: Vendor Portal Frontend - useVendorProfile Hook
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorProfileApi } from '@/features/vendor/api/vendorApi';
import type { VendorProfile, VendorProfileFormData, VendorStats, RecentActivity } from '@/features/vendor/types/vendorTypes';

// Query keys
export const vendorProfileKeys = {
  all: ['vendor-profile'] as const,
  profile: () => [...vendorProfileKeys.all, 'profile'] as const,
  stats: () => [...vendorProfileKeys.all, 'stats'] as const,
  activity: (limit?: number) => [...vendorProfileKeys.all, 'activity', limit] as const,
};

/**
 * Hook for fetching vendor profile
 */
export function useVendorProfile() {
  return useQuery({
    queryKey: vendorProfileKeys.profile(),
    queryFn: () => vendorProfileApi.getProfile(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for updating vendor profile
 */
export function useUpdateVendorProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<VendorProfileFormData>) => vendorProfileApi.updateProfile(data),
    onSuccess: (updatedProfile) => {
      queryClient.setQueryData<VendorProfile>(vendorProfileKeys.profile(), updatedProfile);
    },
  });
}

/**
 * Hook for uploading vendor logo
 */
export function useUploadVendorLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => vendorProfileApi.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
    },
  });
}

/**
 * Hook for uploading vendor banner
 */
export function useUploadVendorBanner() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => vendorProfileApi.uploadBanner(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
    },
  });
}

/**
 * Hook for submitting verification documents
 */
export function useSubmitVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ files, documentType }: { files: File[]; documentType: string }) =>
      vendorProfileApi.submitVerification(files, documentType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: vendorProfileKeys.profile() });
    },
  });
}

/**
 * Hook for fetching vendor statistics
 */
export function useVendorStats() {
  return useQuery({
    queryKey: vendorProfileKeys.stats(),
    queryFn: () => vendorProfileApi.getStats(),
    staleTime: 60 * 1000, // 1 minute
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * Hook for fetching recent activity
 */
export function useVendorActivity(limit = 10) {
  return useQuery({
    queryKey: vendorProfileKeys.activity(limit),
    queryFn: () => vendorProfileApi.getRecentActivity(limit),
    staleTime: 60 * 1000, // 1 minute
  });
}

/**
 * Combined hook for vendor dashboard data
 */
export function useVendorDashboard() {
  const profileQuery = useVendorProfile();
  const statsQuery = useVendorStats();
  const activityQuery = useVendorActivity(5);

  return {
    profile: profileQuery.data,
    stats: statsQuery.data,
    activity: activityQuery.data,
    isLoading: profileQuery.isLoading || statsQuery.isLoading || activityQuery.isLoading,
    isError: profileQuery.isError || statsQuery.isError || activityQuery.isError,
    error: profileQuery.error || statsQuery.error || activityQuery.error,
    refetch: () => {
      profileQuery.refetch();
      statsQuery.refetch();
      activityQuery.refetch();
    },
  };
}

